import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'i18n-js';
import { Button } from 'grab-ui';
import _get from 'lodash/get';
import DivButton from 'components/v2/div-button/div-button';
import { ExceptMobile, Mobile } from 'components/v2/responsive';
import BookAgainMobile from 'views/v2/home/book-again-mobile';
import BookAgainTable from 'components/v2/book-again-table/book-again-table';
import useRebook from 'components/v2/anytime-booking/common/use-rebook';
import { checkBookAgainable } from 'helper/history-order';
import { getDeliveryID } from 'helper/order';
import { userInfoSelector } from 'selectors/app-selector';
import { bookAgainSelector } from 'selectors/new-history-selectors';
import NewHistoryDuck from 'ducks/new-history-duck';
import { getCompanyID } from 'helper/user';
import { cat, custom, event, trackClick } from 'common/utils/instrumentation';
import {
  getCommonFacet,
  SCRIBE_EVENT_PARAMS,
  SCRIBE_HOME_EVENTS,
  SCRIBE_STATE_NAMES,
  scribeTrackEvent,
} from 'applications/scribe/instrumentations/track';

import commonStyles from './common.less';
import styles from './book-again-section.less';

const BookAgainDataTTLInMS = 20 * 60 * 1000; // 20 mins

const BookAgainSection = ({ goToBookingsTab }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(userInfoSelector);
  const { loading, lastUpdated, deliveriesV2 } = useSelector(bookAgainSelector);
  const completedOrders = _get(deliveriesV2, 'deliveries', []);
  const rebookBookings = useRebook({ page: 'home' });
  const bookAgain = useCallback(order => () => rebookBookings([order]), [
    rebookBookings,
  ]);

  const onViewAll = useCallback(
    () => {
      const companyID = getCompanyID(userInfo);
      trackClick(event.HOMEPAGE_CLICKS, cat.GE_HOMEPAGE, 'VIEW_PAST_BOOKINGS', {
        [custom.COMPANY_ID]: companyID,
      });

      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_HOMEPAGE_WEB,
        eventName: SCRIBE_HOME_EVENTS.VIEW_PAST_BOOKINGS,
        facets: {
          ...getCommonFacet(userInfo),
          [SCRIBE_EVENT_PARAMS.PAST_BOOKINGS]: completedOrders
            ? completedOrders.map(getDeliveryID)
            : [],
        },
      });
      goToBookingsTab();
    },
    [goToBookingsTab, userInfo, completedOrders],
  );

  // only refresh after BookAgainDataTTLInMS
  useEffect(() => {
    const now = +new Date();
    if (now - lastUpdated > BookAgainDataTTLInMS) {
      dispatch(NewHistoryDuck.creators.fetchBookAgain());
    }
  }, []);

  return (
    <div className={styles.bookAgainSection}>
      <div className={commonStyles.sectionHeader}>
        <h5 className={commonStyles.title}>
          {I18n.t('v2.homepage.orderOverview.bookAgain')}
        </h5>
        <DivButton className={commonStyles.cta} onClick={onViewAll}>
          {I18n.t('v2.homepage.orderOverview.viewAll')}
        </DivButton>
      </div>
      <Mobile>
        <BookAgainMobile
          completedOrders={completedOrders}
          onClickBookAgain={bookAgain}
        />
      </Mobile>
      <ExceptMobile>
        <BookAgainTable
          orders={completedOrders}
          loading={loading}
          action={order => {
            const isDisabled = !checkBookAgainable(order);
            return (
              <Button
                onClick={bookAgain(order)}
                role="button"
                tabIndex="0"
                onKeyDown={null}
                className={styles.bookAgainBtn}
                disabled={isDisabled}
              >
                {I18n.t('v2.homepage.orderOverview.bookAgain')}
              </Button>
            );
          }}
          pagination={{ pageSize: 10 }}
        />
      </ExceptMobile>
    </div>
  );
};

BookAgainSection.propTypes = {
  goToBookingsTab: PropTypes.func.isRequired,
};

export default BookAgainSection;
